import { v4 as uuidv4 } from 'uuid'
import { ActionsUnion, createAction, getCurrentWindowId } from 'utils'
import { clientActionEvent } from './ClientAction'
import {
  EntityIdAndType,
  EntityTypeEnum,
  ReferenceDataManagementEventCancel,
  ReferenceDataManagementEventCancelBulk,
  ReferenceDataManagementEventCreateClone,
  ReferenceDataManagementEventCreateEdit,
  ReferenceDataManagementEventCreateNew,
  ReferenceDataManagementEventEntityAdd,
  ReferenceDataManagementEventEntityDelete,
  ReferenceDataManagementEventEntityUpdate,
  ReferenceDataManagementEventSubmit,
  ReferenceDataManagementEventSubmitBulk
} from './generated'
import { CorrelatedResponseCallback } from 'store/notification/notificationTypes'
import { EntityWrapper, ReferenceDataManagementEventCreateAndSubmit } from 'api'

export enum WorkflowActionsEnum {
  CREATE_NEW_WORKFLOW = 'CREATE_NEW_WORKFLOW',
  CREATE_EDIT_WORKFLOW = 'CREATE_EDIT_WORKFLOW',
  CREATE_CLONE_WORKFLOW = 'CREATE_CLONE_WORKFLOW',
  ADD_TO_EXISTING_WORKFLOW = 'ADD_TO_EXISTING_WORKFLOW'
}

function createNewWorkflow(entityTypeEnum: EntityTypeEnum, correlatedResponseCallback?: CorrelatedResponseCallback) {
  return createAction(WorkflowActionsEnum.CREATE_NEW_WORKFLOW, { entityTypeEnum, correlatedResponseCallback })
}

function createEditWorkflow(
  entityTypeEnum: EntityTypeEnum,
  entityId: string,
  correlatedResponseCallback?: CorrelatedResponseCallback,
  entityTypeOnly: boolean = false
) {
  return createAction(WorkflowActionsEnum.CREATE_EDIT_WORKFLOW, {
    entityTypeEnum,
    entityId,
    correlatedResponseCallback,
    entityTypeOnly
  })
}

function createCloneWorkflow(
  entityTypeEnum: EntityTypeEnum,
  entityId: string,
  correlatedResponseCallback?: CorrelatedResponseCallback,
  entityTypeOnly: boolean = false
) {
  return createAction(WorkflowActionsEnum.CREATE_CLONE_WORKFLOW, {
    entityTypeEnum,
    entityId,
    correlatedResponseCallback,
    entityTypeOnly
  })
}

function createAddToExistingWorkflow(
  workflowId: string,
  entityTypeEnum: EntityTypeEnum,
  correlatedResponseCallback?: CorrelatedResponseCallback
) {
  return createAction(WorkflowActionsEnum.ADD_TO_EXISTING_WORKFLOW, {
    entityTypeEnum,
    workflowId,
    correlatedResponseCallback
  })
}

function createSubmitWorkflow(workflowId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<ReferenceDataManagementEventSubmit>('ReferenceDataManagementEventSubmit', { workflowId }, sourceWindowId)
}

function createBulkSubmitWorkflows(workflowIds: string[], sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<ReferenceDataManagementEventSubmitBulk>(
    'ReferenceDataManagementEventSubmitBulk',
    { workflowIds },
    sourceWindowId
  )
}

function createCancelWorkflow(workflowId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<ReferenceDataManagementEventCancel>('ReferenceDataManagementEventCancel', { workflowId }, sourceWindowId)
}

function createBulkCancelWorkflows(workflowIds: string[], sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<ReferenceDataManagementEventCancelBulk>(
    'ReferenceDataManagementEventCancelBulk',
    { workflowIds },
    sourceWindowId
  )
}

function createNewReferenceData(entityTypes: EntityTypeEnum[], sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<ReferenceDataManagementEventCreateNew>('ReferenceDataManagementEventCreateNew', { entityTypes }, sourceWindowId)
}

function createEditReferenceData(entityIdAndTypes: EntityIdAndType[], sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<ReferenceDataManagementEventCreateEdit>(
    'ReferenceDataManagementEventCreateEdit',
    { entityIdAndTypes },
    sourceWindowId
  )
}

function createCloneReferenceData(entityIdAndTypes: EntityIdAndType[], sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<ReferenceDataManagementEventCreateClone>(
    'ReferenceDataManagementEventCreateClone',
    { entityIdAndTypes },
    sourceWindowId
  )
}

function createEntityAddReferenceData(entityTypes: EntityTypeEnum[], workflowId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent<ReferenceDataManagementEventEntityAdd>(
    'ReferenceDataManagementEventEntityAdd',
    { entityTypes, workflowId },
    sourceWindowId
  )
}

function createEntityDeleteReferenceData(
  entitiesToDelete: EntityIdAndType[],
  workflowId: string,
  sourceWindowId: string = getCurrentWindowId()
) {
  return clientActionEvent<ReferenceDataManagementEventEntityDelete>(
    'ReferenceDataManagementEventEntityDelete',
    { entitiesToDelete, workflowId },
    sourceWindowId
  )
}

function createEntityUpdateReferenceData<TEntity>(
  payload: EntityIdAndType & { updatedEntity: TEntity; workflowId: string },
  sourceWindowId: string = getCurrentWindowId()
) {
  return clientActionEvent<ReferenceDataManagementEventEntityUpdate>(
    'ReferenceDataManagementEventEntityUpdate',
    {
      entityIdAndType: { entityId: payload.entityId, entityType: payload.entityType },
      updatedEntity: payload.updatedEntity,
      workflowId: payload.workflowId
    },
    sourceWindowId
  )
}

function createEntitiesCreateAndSubmitReferenceData(
  referenceDataEntities: EntityWrapper[],
  entitiesToDelete: EntityIdAndType[],
  sourceWindowId: string = getCurrentWindowId()
) {
  return clientActionEvent<ReferenceDataManagementEventCreateAndSubmit>(
    'ReferenceDataManagementEventCreateAndSubmit',
    {
      force: false,
      throwErrorOnFailure: true,
      referenceDataManagementData: {
        referenceDataEntities,
        entitiesToDelete,
        workflowId: uuidv4(),
        internal: false
      }
    },
    sourceWindowId
  )
}

export const WorkflowActions = {
  createNewWorkflow,
  createEditWorkflow,
  createCloneWorkflow,
  createAddToExistingWorkflow,
  createSubmitWorkflow,
  createBulkSubmitWorkflows,
  createCancelWorkflow,
  createBulkCancelWorkflows,
  createNewReferenceData,
  createEditReferenceData,
  createCloneReferenceData,
  createEntityAddReferenceData,
  createEntityDeleteReferenceData,
  createEntityUpdateReferenceData,
  createEntitiesCreateAndSubmitReferenceData
}

export type WorkflowActions = ActionsUnion<typeof WorkflowActions>
